import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';
import { CommonTranslationKeys, DialogTranslationKeys } from 'intl/keys';
import { Progress, useUploadProgressStore } from 'store/uploadProgress';
import { useDialog } from '../Dialog/common/DialogContext';

interface UploadProgressController {
  clear: () => Promise<void>;
}

export function useUploadProgressController(): UploadProgressController {
  const createDialog = useDialog();

  const clear = useCallback(() => {
    const { files, clearFiles, abortPendingUploads } = useUploadProgressStore.getState();

    if (files.some((file) => file.status === Progress.PENDING)) {
      return createDialog({
        title: (
          <FormattedMessage id={DialogTranslationKeys.titleAbortUpload} />
        ),
        description: (
          <span>
            <FormattedMessage
              id={DialogTranslationKeys.descriptionAbortUpload}
            />
          </span>
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.confirmAbort),
        ],
      }).then(() => {
        abortPendingUploads();
        clearFiles();
      });
    }

    clearFiles();
    return Promise.resolve();
  }, [createDialog]);

  useEffect(() => useUploadProgressStore.getState().subscribeToChannelMessages(), []);

  useEffect(() => {
    function onBeforeUnload(e: any): true | undefined {
      const files = useUploadProgressStore.getState().files;
      const allUploaded = files.every(
        (file) => file.status === Progress.UPLOADED
      );
      if (allUploaded) {
        return void 0;
      }

      e.returnValue = true;
      return true;
    }
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  return { clear };
}
